// AIArc clarification chat — shown during AR file import when the AI needs the user to clarify
// something about the file (which column is the debtor name, whether a bucket is 0-30 vs 31-60,
// whether to exclude subtotal/parent rows when rows sum past the file's own Total, the currency,
// which row is the header, …). AIArc asks one question at a time; when it's confident it returns a
// finalized mapping via onResolved, and the modal imports.
import React, { useEffect, useRef, useState } from "react";
import { Box, Typography, TextField, IconButton, CircularProgress, Button, Chip } from "@mui/material";
import ArrowUpwardIcon from "@mui/icons-material/ArrowUpward";
import AutoAwesomeIcon from "@mui/icons-material/AutoAwesome";
import { clarifyAgingMapping } from "../api/companyApi";

export default function AiClarifyChat({ grid, seedMessage, onResolved, onCancel, importing, schema }) {
  const [messages, setMessages] = useState(() => (seedMessage ? [{ role: "assistant", content: seedMessage }] : []));
  const [input, setInput] = useState("");
  const [busy, setBusy] = useState(false);
  const scrollRef = useRef(null);

  useEffect(() => { if (scrollRef.current) scrollRef.current.scrollTop = scrollRef.current.scrollHeight; }, [messages, busy]);

  const send = async (text) => {
    const clean = String(text || "").trim();
    if (!clean || busy) return;
    const next = [...messages, { role: "user", content: clean }];
    setMessages(next);
    setInput("");
    setBusy(true);
    try {
      // Only the actual Q&A turns go to the model (skip the local seed if it wasn't from the model).
      const convo = next.filter((m) => m.role === "user" || m.role === "assistant").map((m) => ({ role: m.role, content: m.content }));
      const res = await clarifyAgingMapping(grid, convo, schema);
      setMessages((prev) => [...prev, { role: "assistant", content: res.reply || "…" }]);
      if (res.ready && res.mappings) {
        onResolved(res);
      }
    } catch (e) {
      setMessages((prev) => [...prev, { role: "assistant", content: "Sorry — I hit an issue. You can also map the columns manually." }]);
    } finally {
      setBusy(false);
    }
  };

  return (
    <Box sx={{ width: "100%", border: "1px solid #d5e0fb", borderRadius: "12px", overflow: "hidden", background: "#fbfcfe" }}>
      <Box sx={{ display: "flex", alignItems: "center", gap: 1, px: 2, py: 1.25, background: "#f5f8ff", borderBottom: "1px solid #e3e6ee" }}>
        <AutoAwesomeIcon sx={{ color: "#2f57c4", fontSize: 20 }} />
        <Box sx={{ flex: 1 }}>
          <Typography sx={{ fontWeight: 700, color: "#1f2a44", fontSize: 14 }}>AIArc needs a quick clarification</Typography>
          <Typography sx={{ fontSize: 11.5, color: "#7a8398" }}>Answer below and AIArc will finish importing this file correctly.</Typography>
        </Box>
        <Button size="small" onClick={onCancel} sx={{ textTransform: "none", color: "#7a8398" }}>Map manually instead</Button>
      </Box>

      <Box ref={scrollRef} sx={{ maxHeight: 240, overflowY: "auto", px: 2, py: 1.5, display: "flex", flexDirection: "column", gap: 1 }}>
        {messages.map((m, i) => (
          <Box key={i} sx={{
            alignSelf: m.role === "user" ? "flex-end" : "flex-start",
            maxWidth: "88%", px: 1.5, py: 1, borderRadius: "12px",
            background: m.role === "user" ? "#e8efff" : "#fff",
            border: m.role === "user" ? "none" : "1px solid #e6e9f0",
          }}>
            <Typography sx={{ fontSize: 13, lineHeight: 1.5, color: m.role === "user" ? "#2f57c4" : "#1f2a44", whiteSpace: "pre-wrap" }}>{m.content}</Typography>
          </Box>
        ))}
        {(busy || importing) && (
          <Box sx={{ display: "flex", alignItems: "center", gap: 1, alignSelf: "flex-start" }}>
            <CircularProgress size={13} sx={{ color: "#2f57c4" }} />
            <Typography sx={{ fontSize: 12, color: "#7a8398" }}>{importing ? "Importing…" : "AIArc is thinking…"}</Typography>
          </Box>
        )}
      </Box>

      <Box sx={{ display: "flex", gap: 1, alignItems: "center", flexWrap: "wrap", px: 2, pb: 1 }}>
        {["Exclude subtotal / parent rows", "Yes, that's correct", "No"].map((q) => (
          <Chip key={q} label={q} size="small" onClick={() => send(q)} disabled={busy || importing}
            sx={{ fontSize: 11.5, background: "#eef2fb", color: "#2f57c4", "&:hover": { background: "#e0e8fb" } }} />
        ))}
      </Box>
      <Box sx={{ display: "flex", gap: 1, alignItems: "center", p: 1.25, borderTop: "1px solid #eef0f5", background: "#fff" }}>
        <TextField fullWidth size="small" placeholder="Answer AIArc…" value={input}
          onChange={(e) => setInput(e.target.value)} disabled={busy || importing}
          onKeyDown={(e) => { if (e.key === "Enter" && !e.shiftKey) { e.preventDefault(); send(input); } }} />
        <IconButton onClick={() => send(input)} disabled={busy || importing || !input.trim()}
          sx={{ background: "#2f57c4", color: "#fff", "&:hover": { background: "#264aa8" }, "&.Mui-disabled": { background: "#c7cedd", color: "#fff" } }}>
          <ArrowUpwardIcon sx={{ fontSize: 18 }} />
        </IconButton>
      </Box>
    </Box>
  );
}
